/**
 * AddTeamModal Component
 * Modal for adding a user to a team with role selection
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, LoadingSpinner } from '../common';
import { useAddUserTeam, useTeams } from '../../hooks';

export interface AddTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  existingTeamIds: string[];
}

export const AddTeamModal: React.FC<AddTeamModalProps> = ({
  isOpen,
  onClose,
  userId,
  existingTeamIds,
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [role, setRole] = useState('member');

  const { data: allTeams = [], isLoading: isLoadingTeams } = useTeams();
  const addMutation = useAddUserTeam();

  // Filter out teams user is already in
  const availableTeams = allTeams.filter(
    (team) => !existingTeamIds.includes(team.id)
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedTeamId('');
      setRole('member');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTeamId) {
      return;
    }

    try {
      await addMutation.mutateAsync({
        userId,
        data: {
          teamId: selectedTeamId,
          role: role || null,
        },
      });
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleClose = () => {
    if (!addMutation.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add User to Team"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isLoadingTeams ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : availableTeams.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-stone-500 mb-2">No available teams</p>
            <p className="text-sm text-stone-400">
              This user is already assigned to all teams, or no teams have been created yet.
            </p>
          </div>
        ) : (
          <>
            <Select
              label="Team"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              options={[
                { value: '', label: 'Select a team', disabled: false },
                ...availableTeams.map((team) => ({
                  value: team.id,
                  label: team.description ? `${team.name} — ${team.description}` : team.name,
                  disabled: false,
                })),
              ]}
              required
            />

            <Select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              options={[
                { value: '', label: 'No Role', disabled: false },
                { value: 'member', label: 'Member', disabled: false },
                { value: 'lead', label: 'Lead', disabled: false },
                { value: 'manager', label: 'Manager', disabled: false },
              ]}
              helperText="Assign a role within this team"
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={addMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!selectedTeamId || addMutation.isPending}
              >
                {addMutation.isPending ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Adding...
                  </span>
                ) : (
                  'Add to Team'
                )}
              </Button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};
